import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Card,
  Typography,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Chip,
  Stack,
  Skeleton,
} from "@mui/material";
import { Download, DateRange } from "@mui/icons-material";
import { Pie, Bar, Line } from "react-chartjs-2";
import api from "../../utils/api";
import { processWorkOrderComparison, processWorkOrderTypeComparison } from "./utils";
import ExportTab from "./WorkOrdersExportPage";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  ArcElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  ArcElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const adminCardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const softColors = [
  "#A3E4D7",
  "#F9E79F",
  "#AED6F1",
  "#F5B7B1",
  "#D2B4DE",
  "#A9CCE3",
  "#FAD7A0",
  "#A2D9CE",
  "#F5CBA7",
  "#BB8FCE",
];

const GROUP_NAME_BY_CODE = {
  "EH CH HILL": "Estate Homes",
  "EH EDGMT": "Estate Homes",
  SV: "Sommerset Villas",
  SC: "Southmont Cove",
  SW: "Sutton Walk",
  "VILLA HMS": "Villa Homes",
  WF: "Waterford",
  WW: "Wedgewood",
};

const norm = (v) => (v ?? "").toString().trim();
const canon = (v) => norm(v).replace(/\s+/g, " ").toUpperCase();
const isRenter = (v) => /\bRENTER\b/i.test(v);

const normalizeMembershipCode = (v) => {
  const c = canon(v);
  if (/^(SV|SC|SW|WF|WW)\b/.test(c)) {
    return c.match(/^(SV|SC|SW|WF|WW)/)[1];
  }
  return c;
};

const groupNameFor = (raw) => {
  const c = normalizeMembershipCode(raw);
  return GROUP_NAME_BY_CODE[c] || norm(raw);
};

const groupIdFor = (raw) => canon(groupNameFor(raw));

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function offsetDays(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function firstOfMonthISO() {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}

function addDaysISO(dateStr, days) {
  if (!dateStr) return dateStr;
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

const presets = [
  {
    key: "30d",
    label: "Last 30 days",
    get: () => ({ start: offsetDays(30), end: todayISO() }),
  },
  {
    key: "90d",
    label: "Last 90 days",
    get: () => ({ start: offsetDays(90), end: todayISO() }),
  },
  {
    key: "mtd",
    label: "MTD",
    get: () => ({ start: firstOfMonthISO(), end: todayISO() }),
  },
  {
    key: "ytd",
    label: "YTD",
    get: () => ({
      start: `${new Date().getFullYear()}-01-01`,
      end: todayISO(),
    }),
  },
];

const SectionHeader = ({ title, subtitle, right }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: { xs: "column", sm: "row" },
      alignItems: { xs: "flex-start", sm: "center" },
      justifyContent: "space-between",
      gap: 1.5,
      mb: 2,
    }}
  >
    <Box>
      <Typography
        variant="h6"
        sx={{ fontWeight: 700, color: "#223142", mb: subtitle ? 0.5 : 0 }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" sx={{ color: "#6b778c" }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    {right}
  </Box>
);

const StatTile = ({ label, value, sublabel }) => (
  <Card
    elevation={0}
    sx={{
      ...adminCardSx,
      p: 3,
      textAlign: "center",
      height: "100%",
    }}
  >
    <Typography
      variant="overline"
      sx={{
        letterSpacing: 1,
        color: "#6b778c",
        fontWeight: 700,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="h3"
      sx={{
        fontWeight: 800,
        lineHeight: 1.1,
        color: "#223142",
        my: 0.75,
      }}
    >
      {value}
    </Typography>
    {sublabel && (
      <Typography variant="caption" sx={{ color: "#6b778c" }}>
        {sublabel}
      </Typography>
    )}
  </Card>
);

const EmptyState = () => (
  <Card
    elevation={0}
    sx={{
      ...adminCardSx,
      textAlign: "center",
      py: 6,
      px: 3,
    }}
  >
    <Typography variant="h6" sx={{ mb: 1, color: "#223142", fontWeight: 700 }}>
      No data to display
    </Typography>
    <Typography variant="body2" sx={{ color: "#6b778c" }}>
      Try broadening your filters or selecting a different date range.
    </Typography>
  </Card>
);

const WorkOrdersTab = () => {
  const [loading, setLoading] = useState(true);
  const [workOrders, setWorkOrders] = useState([]);
  const [trendView, setTrendView] = useState("month");
  const [comparisonData, setComparisonData] = useState({
    current: [],
    previous: [],
    labels: [],
  });
  const [typeComparisonData, setTypeComparisonData] = useState({
    labels: [],
    current: [],
    previous: [],
  });
  const [activeTab, setActiveTab] = useState(0);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedSubtype, setSelectedSubtype] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [includeMember, setIncludeMember] = useState(true);
  const [membershipType, setMembershipType] = useState("");
  const [membershipTypeOptions, setMembershipTypeOptions] = useState([]);
  const [membershipTypeLoading, setMembershipTypeLoading] = useState(false);
  const [types, setTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [statuses, setStatuses] = useState([]);

  const overviewRef = useRef(null);

  useEffect(() => {
    fetchFilters();
    const { start, end } = presets.find((p) => p.key === "90d").get();
    setStartDate(start);
    setEndDate(end);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchData(true), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    startDate,
    endDate,
    selectedType,
    selectedSubtype,
    selectedLocation,
    propertyId,
    includeMember,
    membershipType,
  ]);

  useEffect(() => {
    const { labels, current, previous } = processWorkOrderComparison(
      workOrders,
      "created_at",
      trendView
    );
    setComparisonData({ labels, current, previous });
  }, [trendView, workOrders]);

  useEffect(() => {
    const { labels, current, previous } = processWorkOrderTypeComparison(
      workOrders,
      "created_at",
      trendView
    );
    setTypeComparisonData({ labels, current, previous });
  }, [trendView, workOrders]);

  const fetchFilters = async () => {
    try {
      const [typesRes, subtypesRes, locationsRes, statusesRes] =
        await Promise.all([
          api.get("/workorder-types"),
          api.get("/workorder-subtypes"),
          api.get("/workorder-locations"),
          api.get("/statuses/workorders"),
        ]);

      setTypes(typesRes.data || []);
      setSubtypes(subtypesRes.data || []);
      setLocations(locationsRes.data || []);
      setStatuses(statusesRes.data || []);
    } catch (err) {
      console.error("Error fetching filter data:", err);
    }
  };

  const buildMembershipOptionsFromRows = (rows) => {
    const groups = new Map();

    for (const o of rows || []) {
      const raw = o?.member?.ClubMembershipTypeCode;
      if (!raw || isRenter(raw)) continue;

      const id = groupIdFor(raw);
      const label = groupNameFor(raw);

      const entry = groups.get(id) || {
        value: id,
        label,
        rawCodes: [],
      };

      entry.rawCodes.push(raw.trim());
      groups.set(id, entry);
    }

    return Array.from(groups.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  };

  const fetchMembershipTypesFromReport = async () => {
    setMembershipTypeLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("start", startDate);
      if (endDate) params.append("end", addDaysISO(endDate, 1));
      if (selectedType) params.append("type_id", selectedType);
      if (selectedSubtype) params.append("subtype_id", selectedSubtype);
      if (selectedLocation) params.append("location_id", selectedLocation);
      if (propertyId.trim()) params.append("property_id", propertyId.trim());
      params.append("includeMember", "1");

      const { data } = await api.get(`/getWorkOrdersReport?${params.toString()}`);
      setMembershipTypeOptions(buildMembershipOptionsFromRows(data || []));
    } catch (e) {
      console.error("membership types (reports api) fetch error:", e);
      setMembershipTypeOptions([]);
    } finally {
      setMembershipTypeLoading(false);
    }
  };

  useEffect(() => {
    if (includeMember) {
      setMembershipTypeOptions(buildMembershipOptionsFromRows(workOrders));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrders, includeMember]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!includeMember) {
        fetchMembershipTypesFromReport();
      }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    includeMember,
    startDate,
    endDate,
    selectedType,
    selectedSubtype,
    selectedLocation,
    propertyId,
  ]);

  const fetchData = async (withFilters = false) => {
    setLoading(true);
    try {
      if (withFilters) {
        const params = new URLSearchParams();
        if (startDate) params.append("start", startDate);
        if (endDate) params.append("end", addDaysISO(endDate, 1));
        if (selectedType) params.append("type_id", selectedType);
        if (selectedSubtype) params.append("subtype_id", selectedSubtype);
        if (selectedLocation) params.append("location_id", selectedLocation);
        if (propertyId.trim()) params.append("property_id", propertyId.trim());
        if (includeMember) params.append("includeMember", "1");

        if (membershipType) {
          const opt = membershipTypeOptions.find((o) => o.value === membershipType);
          if (opt && opt.rawCodes?.length) {
            params.append("club_membership_type_code", opt.rawCodes.join(","));
          }
        }

        const { data } = await api.get(`/getWorkOrdersReport?${params.toString()}`);
        setWorkOrders(data || []);
      } else {
        const ordersRes = await api.get(`/getWorkOrders`);
        setWorkOrders(ordersRes.data || []);
      }
    } catch (error) {
      console.error("Error fetching work orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTypeChange = (e) => {
    setSelectedType(e.target.value);
    setSelectedSubtype("");
    setSelectedLocation("");
  };

  const handleSubtypeChange = (e) => {
    setSelectedSubtype(e.target.value);
    setSelectedLocation("");
  };

  const handleReset = () => {
    setSelectedType("");
    setSelectedSubtype("");
    setSelectedLocation("");
    setPropertyId("");
    setMembershipType("");
    setIncludeMember(true);
    const { start, end } = presets.find((p) => p.key === "90d").get();
    setStartDate(start);
    setEndDate(end);
  };

  const totalWorkOrders = workOrders.length;

  const completedWorkOrders = useMemo(
    () =>
      workOrders.filter(
        (o) => (o.status || "").toLowerCase() === "resolved"
      ).length,
    [workOrders]
  );

  const openWorkOrders = totalWorkOrders - completedWorkOrders;

  const { avgCompletionDays, medianCompletionDays } = useMemo(() => {
    const completed = workOrders.filter(
      (order) => order.resolved_at && order.created_at
    );

    if (completed.length === 0) {
      return { avgCompletionDays: 0, medianCompletionDays: 0 };
    }

    const days = completed
      .map((order) => {
        const created = new Date(order.created_at);
        const resolved = new Date(order.resolved_at);
        return (resolved - created) / (1000 * 60 * 60 * 24);
      })
      .sort((a, b) => a - b);

    const avg = days.reduce((a, b) => a + b, 0) / days.length;
    const mid = Math.floor(days.length / 2);
    const median =
      days.length % 2 === 0 ? (days[mid - 1] + days[mid]) / 2 : days[mid];

    return {
      avgCompletionDays: Number(avg.toFixed(1)),
      medianCompletionDays: Number(median.toFixed(1)),
    };
  }, [workOrders]);

  const statusCounts = useMemo(
    () =>
      workOrders.reduce((acc, order) => {
        const st = order.status || "Unknown";
        acc[st] = (acc[st] || 0) + 1;
        return acc;
      }, {}),
    [workOrders]
  );

  const priorityCounts = useMemo(
    () =>
      workOrders.reduce((acc, order) => {
        const p = order.priority || "Unknown";
        acc[p] = (acc[p] || 0) + 1;
        return acc;
      }, {}),
    [workOrders]
  );

  const typePieCounts = useMemo(() => {
    const acc = {};
    for (const o of workOrders) {
      const type = o.type_title || o.type_name || "Unknown Type";
      acc[type] = (acc[type] || 0) + 1;
    }
    return acc;
  }, [workOrders]);

  const membershipCounts = useMemo(() => {
    const acc = {};
    for (const o of workOrders) {
      const raw = o?.member?.ClubMembershipTypeCode;
      if (!raw || isRenter(raw)) continue;

      const label = groupNameFor(raw);
      acc[label] = (acc[label] || 0) + 1;
    }
    return acc;
  }, [workOrders]);

  const typePieData = useMemo(
    () => ({
      labels: Object.keys(typePieCounts),
      datasets: [
        {
          data: Object.values(typePieCounts),
          backgroundColor: Object.keys(typePieCounts).map(
            (_, i) => softColors[i % softColors.length]
          ),
          borderWidth: 0,
        },
      ],
    }),
    [typePieCounts]
  );

  const statusPieData = useMemo(
    () => ({
      labels: Object.keys(statusCounts),
      datasets: [
        {
          data: Object.values(statusCounts),
          backgroundColor: Object.keys(statusCounts).map(
            (_, i) => softColors[(i + 2) % softColors.length]
          ),
          borderWidth: 0,
        },
      ],
    }),
    [statusCounts]
  );

  const priorityChartData = useMemo(
    () => ({
      labels: Object.keys(priorityCounts),
      datasets: [
        {
          data: Object.values(priorityCounts),
          backgroundColor: softColors,
          borderWidth: 0,
        },
      ],
    }),
    [priorityCounts]
  );

  const membershipChartData = useMemo(
    () => ({
      labels: Object.keys(membershipCounts),
      datasets: [
        {
          label: "Work Orders",
          data: Object.values(membershipCounts),
          backgroundColor: "rgba(47,99,173,0.58)",
          borderRadius: 8,
          barThickness: 18,
        },
      ],
    }),
    [membershipCounts]
  );

  const comparisonChartData = useMemo(
    () => ({
      labels: comparisonData.labels,
      datasets: [
        {
          label: `This ${trendView}`,
          data: comparisonData.current,
          borderColor: "rgba(47,99,173,0.85)",
          backgroundColor: "rgba(47,99,173,0.16)",
          fill: true,
          tension: 0.35,
          pointRadius: 3,
        },
        {
          label: `Last ${trendView}`,
          data: comparisonData.previous,
          borderColor: "rgba(220,38,38,0.82)",
          backgroundColor: "rgba(220,38,38,0.12)",
          fill: true,
          tension: 0.35,
          pointRadius: 3,
        },
      ],
    }),
    [comparisonData, trendView]
  );

  const typeComparisonChartData = useMemo(
    () => ({
      labels: typeComparisonData.labels,
      datasets: [
        {
          label: `This ${trendView}`,
          data: typeComparisonData.current,
          backgroundColor: "rgba(47,99,173,0.68)",
          borderRadius: 8,
          barThickness: 18,
        },
        {
          label: `Last ${trendView}`,
          data: typeComparisonData.previous,
          backgroundColor: "rgba(220,38,38,0.58)",
          borderRadius: 8,
          barThickness: 18,
        },
      ],
    }),
    [typeComparisonData, trendView]
  );

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      datalabels: {
        color: "#223142",
        backgroundColor: "rgba(255,255,255,0.92)",
        borderColor: "rgba(0,0,0,0.12)",
        borderWidth: 1,
        borderRadius: 4,
        font: { weight: "bold", size: 11 },
        padding: 4,
        anchor: "end",
        align: "top",
        display: (ctx) => ctx.dataset.data[ctx.dataIndex] > 0,
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.label}: ${ctx.raw}`,
        },
      },
    },
    scales: {
      x: {
        grid: { color: "rgba(47,99,173,0.08)" },
        ticks: { font: { size: 12 } },
      },
      y: {
        grid: { color: "rgba(47,99,173,0.08)" },
        ticks: { font: { size: 12 } },
      },
    },
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom" },
      datalabels: { display: false },
    },
    scales: {
      x: { grid: { color: "rgba(47,99,173,0.06)" } },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(47,99,173,0.08)" },
      },
    },
  };

  const unifiedPieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 12,
          padding: 10,
          font: { size: 12 },
        },
      },
      datalabels: {
        color: "#111",
        font: { weight: "bold", size: 11 },
        backgroundColor: "rgba(255,255,255,0.92)",
        borderColor: "rgba(0,0,0,0.08)",
        borderWidth: 1,
        borderRadius: 6,
        padding: 5,
        formatter: (value, ctx) => {
          const data = ctx.chart.data.datasets[0].data || [];
          const total = data.reduce((a, b) => a + b, 0);
          const pct = total ? Math.round((value / total) * 100) : 0;
          return `${pct}%`;
        },
        display: (ctx) => ctx.dataset.data[ctx.dataIndex] > 0,
        clip: true,
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const dataset = ctx.dataset.data;
            const total = dataset.reduce((a, b) => a + b, 0);
            const pct = total ? ((ctx.parsed / total) * 100).toFixed(1) : 0;
            return `${ctx.label}: ${ctx.parsed} (${pct}%)`;
          },
        },
      },
    },
  };

  const handleExportOverviewToPDF = async () => {
    if (!overviewRef.current) return;

    const canvas = await html2canvas(overviewRef.current, {
      scale: 2,
      backgroundColor: "#ffffff",
      useCORS: true,
      logging: false,
      windowWidth: document.documentElement.scrollWidth,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    const filename = `WorkOrders_Overview_${new Date()
      .toISOString()
      .slice(0, 10)}.pdf`;
    pdf.save(filename);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Card
        elevation={0}
        sx={{
          ...adminCardSx,
          overflow: "hidden",
          mb: 3,
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            pt: 2.5,
            pb: 1.5,
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.02)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              gap: 2,
              mb: 2,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#223142",
                  mb: 0.5,
                }}
              >
                Work Orders Overview
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c" }}>
                Switch between dashboard overview and export tools.
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExportOverviewToPDF}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Download PDF
            </Button>
          </Box>

          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            aria-label="Work Orders Tabs"
            sx={{
              minHeight: "auto",
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                minHeight: 42,
                color: "#5f6b7a",
                mr: 1,
                px: 2,
              },
              "& .Mui-selected": {
                color: "#2f63ad !important",
                backgroundColor: "rgba(47,99,173,0.08)",
              },
              "& .MuiTabs-indicator": {
                display: "none",
              },
            }}
          >
            <Tab label="Overview" />
            <Tab label="Export" />
          </Tabs>
        </Box>
      </Card>

      {activeTab === 0 && (
        <Box ref={overviewRef}>
          <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile label="Total Work Orders" value={totalWorkOrders} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile label="Resolved" value={completedWorkOrders} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile label="Open" value={openWorkOrders} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile
                label="Avg / Median Days"
                value={`${avgCompletionDays} / ${medianCompletionDays}`}
                sublabel="to resolve"
              />
            </Grid>
          </Grid>

          <Card
            elevation={0}
            sx={{
              ...adminCardSx,
              p: { xs: 2, md: 3 },
              mb: 2.5,
            }}
          >
            <SectionHeader
              title="Filters"
              subtitle="Refine work order reporting by property, member type, type, subtype, location, and date range."
            />

            <Grid container spacing={2.5} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Member / Property ID"
                  value={propertyId}
                  onChange={(e) => setPropertyId(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>ClubMembershipTypeCode</InputLabel>
                  <Select
                    value={membershipType}
                    label="ClubMembershipTypeCode"
                    onChange={(e) => setMembershipType(e.target.value)}
                    disabled={membershipTypeLoading}
                    sx={{ borderRadius: "10px" }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {membershipTypeOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={selectedType}
                    label="Type"
                    onChange={handleTypeChange}
                    sx={{ borderRadius: "10px" }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {types.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth disabled={!selectedType}>
                  <InputLabel>Subtype</InputLabel>
                  <Select
                    value={selectedSubtype}
                    label="Subtype"
                    onChange={handleSubtypeChange}
                    sx={{ borderRadius: "10px" }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {subtypes
                      .filter((s) => s.type_id === Number(selectedType))
                      .map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.subtype_name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth disabled={!selectedType}>
                  <InputLabel>Location</InputLabel>
                  <Select
                    value={selectedLocation}
                    label="Location"
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    sx={{ borderRadius: "10px" }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {locations
                      .filter((l) => l.type_id === Number(selectedType))
                      .map((l) => (
                        <MenuItem key={l.id} value={l.id}>
                          {l.location_name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="Start Date"
                  InputLabelProps={{ shrink: true }}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="End Date"
                  InputLabelProps={{ shrink: true }}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={includeMember}
                        onChange={(e) => setIncludeMember(e.target.checked)}
                      />
                    }
                    label="Include member info"
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={3}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  justifyContent={{ xs: "stretch", md: "flex-end" }}
                >
                  <Button
                    fullWidth
                    variant="outlined"
                    color="secondary"
                    onClick={handleReset}
                    sx={{
                      borderRadius: "10px",
                      textTransform: "none",
                      fontWeight: 700,
                    }}
                  >
                    Reset
                  </Button>

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => fetchData(true)}
                    sx={{
                      backgroundColor: "#2f63ad",
                      borderRadius: "10px",
                      textTransform: "none",
                      fontWeight: 700,
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "#275592",
                        boxShadow: "0 8px 20px rgba(47,99,173,0.22)",
                      },
                    }}
                  >
                    Apply
                  </Button>
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  flexWrap="wrap"
                >
                  {presets.map((p) => {
                    const { start, end } = p.get();
                    const active = startDate === start && endDate === end;

                    return (
                      <Chip
                        key={p.key}
                        icon={<DateRange />}
                        size="small"
                        label={p.label}
                        color={active ? "primary" : "default"}
                        variant={active ? "filled" : "outlined"}
                        onClick={() => {
                          setStartDate(start);
                          setEndDate(end);
                        }}
                        sx={{
                          borderRadius: "10px",
                          fontWeight: 600,
                        }}
                      />
                    );
                  })}
                </Stack>
              </Grid>
            </Grid>
          </Card>

          <Card
            elevation={0}
            sx={{
              ...adminCardSx,
              p: { xs: 2, md: 3 },
              mb: 2.5,
            }}
          >
            <SectionHeader
              title={`Trend: Created (${trendView}) vs last`}
              subtitle="Compare newly created work orders across matching periods."
              right={
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {["day", "week", "month", "year"].map((v) => (
                    <Chip
                      key={v}
                      label={v}
                      size="small"
                      variant={trendView === v ? "filled" : "outlined"}
                      onClick={() => setTrendView(v)}
                      sx={{
                        textTransform: "capitalize",
                        borderRadius: "10px",
                        fontWeight: 600,
                      }}
                    />
                  ))}
                </Stack>
              }
            />

            {loading ? (
              <Skeleton variant="rounded" height={320} />
            ) : workOrders.length === 0 ? (
              <EmptyState />
            ) : (
              <Box sx={{ height: 320 }}>
                <Line data={comparisonChartData} options={lineOptions} />
              </Box>
            )}
          </Card>

          <Grid container spacing={2.5}>
            <Grid item xs={12} md={8}>
              <Card
                elevation={0}
                sx={{
                  ...adminCardSx,
                  p: { xs: 2, md: 3 },
                  height: "100%",
                }}
              >
                <SectionHeader
                  title="Work Orders by Type"
                  subtitle="Comparison of work order type volume for this period versus the previous one."
                />
                {loading ? (
                  <Skeleton variant="rounded" height={320} />
                ) : workOrders.length === 0 ? (
                  <EmptyState />
                ) : (
                  <Box sx={{ height: 320 }}>
                    <Bar data={typeComparisonChartData} options={barOptions} />
                  </Box>
                )}
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                elevation={0}
                sx={{
                  ...adminCardSx,
                  p: { xs: 2, md: 3 },
                  height: "100%",
                }}
              >
                <SectionHeader
                  title="Work Orders by Status"
                  subtitle="Current status distribution as a bar chart."
                />
                {loading ? (
                  <Skeleton variant="rounded" height={320} />
                ) : workOrders.length === 0 ? (
                  <EmptyState />
                ) : (
                  <Box sx={{ height: 320 }}>
                    <Bar
                      data={{
                        labels: Object.keys(statusCounts),
                        datasets: [
                          {
                            label: "Count",
                            data: Object.values(statusCounts),
                            backgroundColor: Object.keys(statusCounts).map(
                              (_, i) => softColors[i % softColors.length]
                            ),
                            borderRadius: 8,
                            barThickness: 24,
                          },
                        ],
                      }}
                      options={barOptions}
                    />
                  </Box>
                )}
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                sx={{
                  ...adminCardSx,
                  p: { xs: 2, md: 3 },
                }}
              >
                <SectionHeader
                  title="Work Orders by Type"
                  subtitle="Pie breakdown of work order categories."
                />
                {loading ? (
                  <Skeleton variant="rounded" height={360} />
                ) : workOrders.length === 0 ? (
                  <EmptyState />
                ) : (
                  <Box sx={{ height: 360, maxWidth: 520, mx: "auto" }}>
                    <Pie data={typePieData} options={unifiedPieOptions} />
                  </Box>
                )}
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                sx={{
                  ...adminCardSx,
                  p: { xs: 2, md: 3 },
                }}
              >
                <SectionHeader
                  title="Work Orders by Status"
                  subtitle="Pie breakdown of current work order statuses."
                />
                {loading ? (
                  <Skeleton variant="rounded" height={360} />
                ) : workOrders.length === 0 ? (
                  <EmptyState />
                ) : (
                  <Box sx={{ height: 360, maxWidth: 520, mx: "auto" }}>
                    <Pie data={statusPieData} options={unifiedPieOptions} />
                  </Box>
                )}
              </Card>
            </Grid>
          </Grid>

          <Card
            elevation={0}
            sx={{
              ...adminCardSx,
              p: { xs: 2, md: 3 },
              mt: 2.5,
              mb: 2.5,
            }}
          >
            <SectionHeader
              title="By ClubMembershipTypeCode"
              subtitle="Grouped by member club membership type code."
            />
            {!includeMember && (
              <Typography variant="body2" sx={{ color: "#6b778c", mb: 1.5 }}>
                Toggle “Include member info” in filters to enable this chart.
              </Typography>
            )}
            <Box sx={{ height: 320 }}>
              <Bar data={membershipChartData} options={barOptions} />
            </Box>
          </Card>

          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                sx={{
                  ...adminCardSx,
                  p: { xs: 2, md: 3 },
                }}
              >
                <SectionHeader
                  title="By Priority"
                  subtitle="Priority distribution across the selected work orders."
                />
                {loading ? (
                  <Skeleton variant="rounded" height={360} />
                ) : workOrders.length === 0 ? (
                  <EmptyState />
                ) : (
                  <Box sx={{ height: 360, maxWidth: 520, mx: "auto" }}>
                    <Pie data={priorityChartData} options={unifiedPieOptions} />
                  </Box>
                )}
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {activeTab === 1 && <ExportTab />}
    </Box>
  );
};

export default WorkOrdersTab;